import { FormActions } from "../../components/common/FormActions";
import {
  createViewEdit,
  formatCurrency,
  formatDateTime,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_DYNAMIC_QR_CODE } from "../../utils/urls";
import { IFormSection } from "../../types/common";
import { API_QR_CODE } from "../../utils/apis";
import { BooleanTags } from "../../components/common/registerUtils";
import { PrintOutlined } from "@mui/icons-material";
import { message } from "antd";
import styled from "styled-components";
import { BASE_API_URL } from "../../config";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

const url = URL_DYNAMIC_QR_CODE;

const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const useDynamicQRcode = () => {
  const { authToken } = useAuth();

  const tableColumns = () => [
    { id: "amount", header: "Amount" },
    { id: "reason_for_payment", header: "Reason for payment" },
    { id: "type_of_payment", header: "Type of payment" },
    { id: "customer_name", header: "Customer name" },
    { id: "customer_email", header: "Customer email" },
    { id: "phone_number", header: "Phone number" },
    { id: "unique_reference", header: "Unique reference" },
    { id: "actions", header: "Actions" },
  ];

  const downloadPDF = async (id: string) => {
    try {
      const response = await axios({
        method: "GET",
        url: `${BASE_API_URL}${API_QR_CODE}${id}/generate-payment-pdf/`,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qr_code_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success("PDF downloaded successfully");
    } catch (error) {
      message.error("Error downloading PDF");
    }
  };

  const tableData = (data: any) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        actions: (
          <FormActions
            actions={[
              ...createViewEdit(url, item?.id, true),
              {
                label: "Print QR Code",
                custom: true,
                icon: PrintOutlined,
                modalTitle: "QR Code Payment",
                modalOkText: "Print PDF",
                modalOkType: "primary" as const,
                modalCancelText: "Cancel",
                modalWidth: 600,
                modalContent: () => (
                  <QRCodeModalContent>
                    <h3>Scan QR Code</h3>
                    <QRCodeImage src={item.qr_code_image} alt="QR Code" />
                    <QRCodeData>{item.qr_code_data}</QRCodeData>
                  </QRCodeModalContent>
                ),
                onModalOk: async () => {
                  await downloadPDF(item?.id);
                },
              },
            ]}
          />
        ),
      };
    }),
  ];

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "OnePayX charges Details",
          fields: {
            merchant: item?.merchant,
            merchant_name: item?.merchant_name,
            performed_by: item?.performed_by,
            created_at: formatDateTime(item?.created_at),
            updated_at: formatDateTime(item?.updated_at),
            declaration: <BooleanTags status={item?.declaration} />,
            deleted: <BooleanTags status={item?.deleted} />,
            amount: formatCurrency(item?.amount),
            reason_for_payment: item?.reason_for_payment,
            type_of_payment: item?.type_of_payment,
            customer_name: item?.customer_name,
            customer_email: item?.customer_email,
            is_printed: <BooleanTags status={item?.is_printed} />,
            phone_number: item?.phone_number,
            unique_reference: item?.unique_reference,
            updated_by: item?.updated_by,
            created_by: item?.created_by,
          },
        },
      ],
    };
  };

  const formRoute = getForm(url);

  return { tableData, tableColumns, formRoute, viewData };
};

export const useDynamicQRcodeForm = () => {
  const sections: IFormSection[] = [
    {
      title: "Dynamic QR Code Details",
      fields: [
        {
          field_name: "amount",
          label: "Amount",
          type: "number",
          props: { required: true },
        },
        {
          field_name: "reason_for_payment",
          label: "Reason for payment",
          type: "textarea",
          props: { required: false },
        },
        {
          field_name: "type_of_payment",
          label: "Type of payment",
          type: "text",
          visible: false,
          props: { required: true, disabled: true },
        },
        {
          field_name: "customer_name",
          label: "Customer name",
          type: "text",
          props: { required: false },
        },
        {
          field_name: "customer_email",
          label: "Customer email",
          type: "email",
          props: { required: false },
        },
        {
          field_name: "phone_number",
          label: "Phone number",
          type: "tel",
          props: { required: false },
        },
        {
          field_name: "unique_reference",
          label: "Unique reference",
          type: "text",
          visible: false,
          props: { required: false },
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  return {
    sections,
    defaultValues: {
      type_of_payment: "dynamic",
      unique_reference: generateUUID(),
    },
    redirect,
  };
};

export const submitDynamicQRcode = (payload: any, id: string) => {
  //   UPDATE
  if (id) {
    return {
      id,
      api: API_QR_CODE,
      params: {
        ...payload,
      },
    };
  }
  //   CREATE
  return {
    api: API_QR_CODE,
    params: {
      ...payload,
    },
  };
};

// Styled Components for QR Code Modal
const QRCodeModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  text-align: center;

  h3 {
    margin-bottom: 20px;
    color: #262626;
    font-size: 18px;
  }

  p {
    color: #595959;
  }
`;

const QRCodeImage = styled.img`
  max-width: 300px;
  width: 100%;
  height: auto;
  margin: 20px 0;
  border: 2px solid #d9d9d9;
  border-radius: 8px;
  padding: 10px;
  background: white;
`;

const QRCodeData = styled.p`
  font-size: 12px;
  color: #8c8c8c;
  word-break: break-all;
  max-width: 400px;
  margin-top: 10px;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
`;
